"use client";
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState, useTransition } from "react";
import { SiteSearchContext, SiteSearchContextType } from "./SiteSearchContext";
import { useQueryParams } from "@/lib/useQueryParams";
import { searchSchemas } from "../searchSchema";
import { useRouter } from "nextjs-toploader/app";
import { useBreakPoints } from "@/lib/useBreakPoints";
import { Color, SBrandDto, SearchQuery } from "@/__generated__/graphql";
import { parseBrandIds } from "./brandFilterSchema";
import _ from "lodash";
import { CategoryDtoWithHref } from "./SearchSideFilters/types/CategoryDtoWithHref";
import { getSearchQueryKey, getSearchVariables } from "@/app/(main)/categories/[...slugs]/getSearchQueryKey";
import { useGqlClient } from "@/Providers/GQLProvider";
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { undefined } from "zod";
import { useInView } from "react-intersection-observer";
import { useParams, useSearchParams } from "next/navigation";
import { getProductsByFilter } from "../../categories/[...slugs]/getProductsByFilter";
import { parseTagQuery } from "@/lib/normalizeHashtag";
type Props = {
  schemaName: keyof typeof searchSchemas;
  sortItems: { value: string; name: string }[];
  totalProductsCount: number;
  categoryNodes: CategoryDtoWithHref[];
  targetCategory: CategoryDtoWithHref | null;
  totalPages: number;
  search?: string | null;
  colors?: Color[] | null;
  brands?: SBrandDto[] | null;
} & PropsWithChildren;
export default function SiteSearchProvider(props: Props) {
  const { slugs } = useParams();

  const maxInfinitePages = 5;
  const [endOfListRef, inView] = useInView({ rootMargin: "400px" });
  const { children, sortItems, totalProductsCount, schemaName, colors, brands, totalPages, categoryNodes, targetCategory, search } = props;
  const [isPending, startTransition] = useTransition();
  const { mdAndDown } = useBreakPoints();
  const { schema, defaults } = searchSchemas[schemaName];

  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();
  const { queryParams, getPath } = useQueryParams({
    schema: schema,
    defaultValues: defaults,
  });
  const variables = getSearchVariables(queryParams, (slugs as string[]) ?? []);
  const queryKey = useMemo(() => getSearchQueryKey(gqlClient.Search.name, queryParams, (slugs as string[]) ?? []), [queryParams, slugs]);
  const {
    data,
    isFetching: isFetchingNewPageItems,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey,
    placeholderData: keepPreviousData,
    
    queryFn: async (context) => {
      variables.page = context.pageParam;
      return getProductsByFilter(variables);
    },
    initialPageParam: queryParams.page,
    getNextPageParam: (lastPage, pages) => lastPage.search.page + 1,
  });
  const items = useMemo(() => {
    return data ? data.pages.flatMap((a) => a.search).flatMap((a) => a.items) : [];
  }, [data]);

  useEffect(() => {
    if (inView && queryParams.page === 1 && data && data.pages.length < maxInfinitePages && !isFetchingNewPageItems) {
      if (data.pages[data.pages.length - 1].search.hasNextPage) {
        fetchNextPage();
      }
    }
  }, [inView, isFetchingNewPageItems, data]);

  const currentPage = data?.pageParams[data?.pageParams.length - 1] as number;
  const selectedTags = parseTagQuery(queryParams.tag) ?? [];
  const selectedColors = queryParams.filter?.colors;
  const selectedBrands = queryParams.filter?.brands;
  const activeSort = queryParams.sort;
  const inStock = queryParams.filter?.inStock;
  const hasOffer = queryParams.filter?.hasOffer;
  const minPrice = queryParams.filter?.minPrice;
  const maxPrice = queryParams.filter?.maxPrice;
  const router = useRouter();
  const scrollToTop = useCallback(() => {
    const element = document.getElementById("site-search-section");

    if (element) {
      const offset = !mdAndDown ? 64 : 180;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset - 50;
      setTimeout(() => {
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }, 500);
    }
  }, []);

  const handleRemoveTag = useCallback(
    (tag: string, scroll: boolean = true) => {
      const next = selectedTags.filter((item) => item !== tag);
      startTransition(() => {
        router.push(
          getPath({
            page: 1,
            tag: next.length ? next.join(",") : undefined,
          }),
          { scroll: false },
        );
      });
      if (scroll) {
        scrollToTop();
      }
    },
    [selectedTags, getPath, router, scrollToTop],
  );

  const handleSearchChange = useCallback(
    (input: string, scroll: boolean = true) => {
      if (queryParams.search?.trim() === input.trim()) {
        return;
      }
      startTransition(() => {
        router.push(
          getPath({
            page: 1,
            search: input ?? null,
            filter: {
              ...queryParams.filter,
            },
          }),
          { scroll: false },
        );
      });
      if (scroll) {
        scrollToTop();
      }
    },
    [queryParams],
  );

  const handleSortChange = useCallback(
    (newSort: string, scroll: boolean = true) => {
      const isSameSort = activeSort === newSort;
      if (isSameSort) {
        return;
      }
      const item = sortItems.find((a) => a.value === newSort);
      if (!item) {
        return;
      }
      startTransition(() => {
        router.push(
          getPath({
            page: 1,
            sort: item.value,
          }),
          { scroll: false },
        );
      });
      if (scroll) {
        scrollToTop();
      }
    },
    [activeSort, sortItems],
  );
  const handleColorsChange = useCallback(
    (colors?: number[], scroll: boolean = true) => {
      // const isSame = selectedColors?.toString() === colors?.toString();
      // if (isSame) {
      //   return;
      // }

      startTransition(() => {
        router.push(
          getPath({
            page: 1,
            filter: {
              ...queryParams.filter,
              // @ts-ignore
              colors: colors && colors?.length > 0 ? _.uniq(colors) : undefined,
            },
          }),
          { scroll: false },
        );
      });
      if (scroll) {
        scrollToTop();
      }
    },
    [selectedColors, queryParams, colors],
  );
  const handleBrandsChange = useCallback(
    (brandIds?: number[], scroll: boolean = true) => {
      startTransition(() => {
        router.push(
          getPath({
            page: 1,
            filter: {
              ...queryParams.filter,
              brands: parseBrandIds(brandIds),
            },
          }),
          { scroll: false },
        );
      });
      if (scroll) {
        scrollToTop();
      }
    },
    [selectedBrands, queryParams, brands],
  );
  const handleInStockChange = useCallback(
    (value?: boolean, scroll: boolean = true) => {
      const isSame = inStock === value;
      if (isSame) {
        return;
      }

      startTransition(() => {
        router.push(
          getPath({
            page: 1,
            filter: {
              ...queryParams.filter,
              inStock: value === true || null,
            },
          }),
          { scroll: false },
        );
      });
      if (scroll) {
        scrollToTop();
      }
    },
    [inStock, queryParams],
  );
  const handleHasOfferChange = useCallback(
    (value?: boolean, scroll: boolean = true) => {
      const isSame = hasOffer === value;
      if (isSame) {
        return;
      }

      startTransition(() => {
        router.push(
          getPath({
            page: 1,
            filter: {
              ...queryParams.filter,
              hasOffer: value === true || null,
            },
          }),
          { scroll: false },
        );
      });
      if (scroll) {
        scrollToTop();
      }
    },
    [hasOffer, queryParams],
  );
  const handleMinPriceChange = useCallback(
    (value?: number | null, scroll: boolean = true) => {
      const isSame = minPrice === value;
      if (isSame) {
        return;
      }

      startTransition(() => {
        router.push(
          getPath({
            page: 1,
            filter: {
              ...queryParams.filter,
              minPrice: value || null,
            },
          }),
          { scroll: false },
        );
      });
      if (scroll) {
        scrollToTop();
      }
    },
    [minPrice, queryParams],
  );
  const handleMaxPriceChange = useCallback(
    (value?: number | null, scroll: boolean = true) => {
      const isSame = maxPrice === value;
      if (isSame) {
        return;
      }

      startTransition(() => {
        router.push(
          getPath({
            page: 1,
            filter: {
              ...queryParams.filter,
              maxPrice: value || null,
            },
          }),
          { scroll: false },
        );
      });
      if (scroll) {
        scrollToTop();
      }
    },
    [maxPrice, queryParams],
  );
  const handlePriceRangeRemove = useCallback(
    (scroll: boolean = true) => {
      startTransition(() => {
        router.push(
          getPath({
            page: 1,
            filter: {
              ...queryParams.filter,
              maxPrice: null,
              minPrice: null,
            },
          }),
          { scroll: false },
        );
      });
      if (scroll) {
        scrollToTop();
      }
    },
    [maxPrice, minPrice, queryParams],
  );
  const handlePriceRangeChange = useCallback(
    (min?: number | null, max?: number | null, scroll: boolean = true) => {
      startTransition(() => {
        router.push(
          getPath({
            page: 1,
            filter: {
              ...queryParams.filter,
              maxPrice: max,
              minPrice: min,
            },
          }),
          { scroll: false },
        );
      });
      if (scroll) {
        scrollToTop();
      }
    },
    [maxPrice, minPrice, queryParams],
  );

  const handlePageChange = useCallback(
    (page: number, scroll: boolean = true) => {
      startTransition(() => {
        router.push(
          getPath({
            page: page,
          }),
          { scroll: false },
        );
      });
      if (scroll) {
        scrollToTop();
      }
    },
    [currentPage, totalPages],
  );

  const getPagePath = useCallback((page: number) => {
    return getPath({
      ...queryParams,
      page,
    });
  }, []);
  return (
    <SiteSearchContext.Provider
      value={{
        items: items as any,
        categoryNodes,
        targetCategory,
        getPagePath,
        currentPage,
        totalPages,
        colors: colors,
        selectedColors,
        brands,
        selectedBrands,
        sortItems: sortItems,
   
        totalProductsCount: totalProductsCount,
        handleSortChange,
        handleColorsChange,
        handleBrandsChange,
        isPending,
        inStock,
        handleInStockChange,
        hasOffer,
        handleHasOfferChange,
        minPrice,
        handleMinPriceChange,
        maxPrice,
        handleMaxPriceChange,
        handlePriceRangeRemove,
        handlePriceRangeChange,
        handlePageChange,
        endOfListRef,
        isFetchingNewPageItems,
        maxInfinitePages,
        search: search,
        selectedTags,
        handleRemoveTag,
        activeSort: activeSort,
        handleSearchChange,
      }}
    >
      {children}
    </SiteSearchContext.Provider>
  );
}
